import { Alert, Box, Button, Stack, Typography } from "@mui/material"
import { FC, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate, useParams } from "react-router"
import {
  useGetSl1ImportFleetQuery,
  useGetSl1ImportJobsQuery,
} from "src/features/things/sl1ImportApi"
import SaLayout from "./SaLayout"
import {
  Sl1AnomalyTable,
  Sl1FlaggedRows,
} from "./sl1Import/Sl1AnomalyTable"
import {
  eyebrowSx,
  fmtBytes,
  fmtNum,
  fmtStamp,
  panelSx,
  Sl1JobStatusChip,
  Sl1Tile,
} from "./sl1Import/Sl1ImportChrome"
import Sl1ReportLoader from "./sl1Import/Sl1ReportLoader"
import { sl1WindowLabel } from "./sl1Import/sl1WindowLabel"
import Sl1ResolutionBar from "./sl1Import/Sl1ResolutionBar"
import { useSl1ReportSource } from "./sl1Import/useSl1ReportSource"

/**
 * Screen 3 — one run's report (LOCKER-382 §D-4.3).
 *
 * Splits into two halves on purpose, and the split is the design's whole answer to
 * "the findings do not fit on the Thing":
 *
 *  - **Above the fold, from the JOB RECORD.** Verdict, counts, window, build,
 *    status. Always present, always cheap, and still correct after the report body
 *    has been deleted or aged out. This is what `counts` is denormalised FOR.
 *  - **Below it, from the REPORT BODY.** The resolution ledger, the anomalies and
 *    the flagged rows — thousands of items that were never going to fit a 400 KB
 *    DynamoDB item.
 *
 * The second half is empty until a report is loaded, and the page says why rather
 * than rendering an empty table that looks like a clean run. An empty table is the
 * single most misleading thing this screen could show: "no anomalies" and "no
 * report loaded" must never look alike.
 */
export const SaSl1ImportReportPage: FC = () => {
  const { jobId } = useParams<{ jobId: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const { data, isLoading } = useGetSl1ImportJobsQuery({ limit: 500 })
  const { data: fleetData } = useGetSl1ImportFleetQuery({ limit: 500 })

  const job = useMemo(
    () => (data?.things ?? []).find((j) => j.jobId === jobId),
    [data, jobId],
  )
  const providerName = useMemo(() => {
    if (!job) return undefined
    const f = (fleetData?.things ?? []).find(
      (x) => x.sl2TenantId === job.providerTenantId || x.sl1TenantId === job.sl1TenantId,
    )
    return f?.providerName
  }, [fleetData, job])

  const source = useSl1ReportSource(job)

  if (isLoading) {
    return (
      <SaLayout selectedMenuItemLabelKey="sl1Import">
        <Box sx={{ p: 5 }}>
          <Typography variant="body2">{t("sl1LoadingJobs")}</Typography>
        </Box>
      </SaLayout>
    )
  }

  if (!job) {
    return (
      <SaLayout selectedMenuItemLabelKey="sl1Import">
        <Box sx={{ p: 5 }}>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>
            {t("sl1JobNotFound")}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
            {t("sl1JobNotFoundHint", { jobId })}
          </Typography>
          <Button size="small" onClick={() => navigate("/sa/sl1-import")}>
            {t("sl1BackToConsole")}
          </Button>
        </Box>
      </SaLayout>
    )
  }

  const report = source.report

  return (
    <SaLayout selectedMenuItemLabelKey="sl1Import">
      <Box sx={{ maxWidth: 1400, mx: "auto", px: { xs: 3, md: 5 }, py: { xs: 4, md: 5 } }}>
        <Button size="small" onClick={() => navigate("/sa/sl1-import?tab=jobs")} sx={{ mb: 1 }}>
          ← {t("sl1BackToJobs")}
        </Button>

        <Box
          sx={{
            ...panelSx,
            borderLeft: 4,
            borderLeftColor:
              job.status === "BLOCKED" || job.status === "FAILED"
                ? "error.main"
                : job.status === "COMPLETED"
                  ? "success.main"
                  : "info.main",
            p: 2.5,
            mb: 3,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, fontFamily: "monospace" }}>
              {job.jobId}
            </Typography>
            <Sl1JobStatusChip status={job.status} size="medium" />
            <Typography sx={{ color: "text.secondary" }}>
              {providerName ?? job.providerTenantId}
            </Typography>
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
              gap: 2,
              mt: 2.5,
            }}
          >
            {(
              [
                ["sl1FieldKind", t(job.kind === "apply" ? "sl1KindApply" : "sl1KindRecon")],
                ["sl1FieldWindow", sl1WindowLabel(job.window, t)],
                ["sl1FieldSl1Tenant", job.sl1TenantId ?? "—"],
                ["sl1FieldSl2Tenant", job.providerTenantId],
                // Provenance: an operator-run script's numbers are only
                // trustworthy if you know which build produced them.
                ["sl1FieldBuild", `${job.scriptVersion} · ${job.gitCommit}`],
                ["sl1FieldRanBy", job.triggeredBy ?? "—"],
                ["sl1FieldStarted", fmtStamp(job.startedAt)],
                ["sl1FieldFinished", fmtStamp(job.finishedAt)],
                ["sl1FieldPlanDigest", job.planDigest ? job.planDigest.slice(0, 16) : "—"],
                [
                  "sl1FieldReport",
                  job.reportKey
                    ? `${fmtBytes(job.reportBytes)}${job.reportIsLocal ? ` · ${t("sl1LocalFile")}` : ""}`
                    : "—",
                ],
              ] as const
            ).map(([k, v]) => (
              <Box key={k}>
                <Typography sx={eyebrowSx}>{t(k)}</Typography>
                <Typography
                  sx={{ fontSize: "0.8rem", fontFamily: "monospace", wordBreak: "break-all" }}
                >
                  {v}
                </Typography>
              </Box>
            ))}
          </Box>

          {job.error && (
            <Alert severity="error" variant="outlined" sx={{ mt: 2 }}>
              {job.error}
            </Alert>
          )}
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
            gap: 1.5,
            mb: 3,
          }}
        >
          <Sl1Tile label={t("sl1SegFound")} value={fmtNum(job.counts?.found)} />
          <Sl1Tile
            label={t("sl1SegMatchedExact")}
            value={fmtNum(job.counts?.matchedExact)}
            dotColor="success.main"
          />
          <Sl1Tile
            label={t("sl1SegMatchedFallback")}
            value={fmtNum(job.counts?.matchedFallback)}
            dotColor="success.light"
            hint={t("sl1MatchedFallbackHint")}
          />
          <Sl1Tile
            label={t("sl1SegUnmatched")}
            value={fmtNum(job.counts?.unmatched)}
            dotColor="primary.main"
          />
          <Sl1Tile
            label={t("sl1SegReplacesHusk")}
            value={fmtNum(job.counts?.replacesHusk)}
            dotColor="warning.main"
            hint={t("sl1ReplacesHuskHint")}
          />
          <Sl1Tile
            label={t("sl1SegReview")}
            value={fmtNum(job.counts?.review)}
            dotColor="info.main"
          />
          <Sl1Tile
            label={t("sl1SegBlocked")}
            value={fmtNum(job.counts?.blocked)}
            dotColor="error.main"
          />
        </Box>

        <Stack spacing={3}>
          <Sl1ReportLoader job={job} source={source} />

          {report ? (
            <>
              <Alert
                severity={
                  report.verdict.verdict === "blocked"
                    ? "error"
                    : report.verdict.verdict === "needs_review"
                      ? "info"
                      : "success"
                }
                variant="outlined"
              >
                <strong>{t(`sl1Verdict_${report.verdict.verdict}`)}</strong>
                {" — "}
                {report.verdict.because}
              </Alert>
              <Sl1ResolutionBar report={report} />
              <Sl1AnomalyTable anomalies={report.anomalies} />
              <Sl1FlaggedRows findings={report.findings} />
            </>
          ) : (
            // NOT an empty table. "No anomalies" and "no report loaded" must
            // never look alike — an empty table here would read as a clean run.
            <Box sx={{ ...panelSx, p: 5, textAlign: "center" }}>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {t("sl1NoReportLoaded")}
              </Typography>
            </Box>
          )}
        </Stack>
      </Box>
    </SaLayout>
  )
}

export default SaSl1ImportReportPage
